import {
    Amount, AmountFromJSON,
    ProviderPatientClaimStatementSimpleProviderInfo,
    ProviderPatientStatement
} from "../../../api";
import React from "react";
import * as S from "./patient-statement-details.styles";
import {ActionMenuBadge} from "../../badge";
import {Link, useHistory} from 'react-router-dom';
import DialogManager, {ICommonDialogContextProps} from "./dialog-manager";
import {StandardDialog} from "./standard-dialog";
import * as Yup from "yup";
import {ISelectOption, SelectField} from "../../base";


export interface IPatientStatementDetailsProps {
    patientStatement: ProviderPatientStatement,
    printMode?: boolean,
    showActions?: boolean,
    dialogContext?: ICommonDialogContextProps,
    onAllocate?(collectionPaymentSourceId: string, amount: Amount, claimId: string): Promise<void>,
    onUpdateCollectionPayment?(amount: Amount, collectionPaymentId: string, claimId: string): Promise<void>
}

interface StatementRow {
    id: string,
    date?: string,
    text?: string,
    charge?: string,
    credit?: string,
    balance?: string,
    dateKey: string,
    rowType?: string,
    claimId?: string,
    collectionPaymentSourceId?: string,
    collectionPaymentId?: string,
    pendingAdjudication?: boolean
}



export const PatientStatementDetails: React.FC<IPatientStatementDetailsProps> = (props: IPatientStatementDetailsProps) => {

    const patientStatement = props.patientStatement;

    const statementRows: StatementRow[] = [];

    let hasUnallocatedPaymentSources: boolean = false;

    const history = useHistory();

    const dialogManager = new DialogManager(props.dialogContext);

    function getDateString(fromDos?: string, throughDos?: string): string {
        if(!fromDos && !throughDos) {
            return '';
        } else if(fromDos && !throughDos) {
            return fromDos;
        } else if(!fromDos && throughDos) {
            return throughDos;
        } else if(fromDos && throughDos && fromDos === throughDos) {
            return fromDos;
        } else {
            return fromDos + ' - ' + throughDos;
        }
    }

    function getProviderString(renderingProviders?: Array<ProviderPatientClaimStatementSimpleProviderInfo>): string {
        if(renderingProviders) {
            let renderingProviderString: string = '';
            for(const renderingProvider of renderingProviders) {
                if(renderingProviderString.length > 0) {
                    renderingProviderString+= ', ';
                }
                renderingProviderString+= ((renderingProvider.firstName) + ' ' + (renderingProvider.lastName));
            }
            return renderingProviderString;
        } else {
            return '';
        }
    }

    function getCurrentDate(): string {
        const today = new Date();

        const month = String(today.getMonth() + 1).padStart(2, '0');  // months are 0-indexed in JavaScript
        const day = String(today.getDate()).padStart(2, '0');
        const year = today.getFullYear();

        return `${month}/${day}/${year}`;
    }

    function getDateKey(dateString: string|undefined, keySuffix: string): string {
        if(dateString && dateString.length > 0) {
            const dateParts: string[] = dateString.split("-");
            if(dateParts.length > 1) {
                return dateString.replace(/-/g, "") + (keySuffix ? keySuffix : '');
            } else {
                return keySuffix;
            }
        } else {
            return keySuffix;
        }
    }

    const dateGroupCache: Map<string, number> = new Map<string, number>();

    //This call has side effects. If you call it, it will decrement the date group starting at X for a given date and return the key.
    function getDateGroup(dateStringParam: string|undefined): string {
        if(!dateStringParam) {
            return '';
        }
        const dateString = getDateKey(dateStringParam.toString(), '');
        if(!dateGroupCache.has(dateString)) {
            dateGroupCache.set(dateString, 0);
        }
        const dateGroupCacheInt = dateGroupCache.has(dateString) ? dateGroupCache.get(dateString) : 0;
        if(dateGroupCacheInt !== undefined) {
            dateGroupCache.set(dateString, dateGroupCacheInt + 1);
            return dateString + String.fromCharCode("X".charCodeAt(0) - (dateGroupCacheInt));
        }
        return dateString + 'X';
    }

    const handleOpenClaim = (claimId: string) => {
        history.push(`/claims/${claimId}`);
    };

    const handleEdit = (collectionPaymentId: string, originalAmount: string, claimId: string) => {
        const schema = Yup.object().shape({
            amount: Yup.string().required('Amount is required'),
            claimId: Yup.string().required('Claim is required')
        });

        const initialValues = {
            amount: originalAmount,
            claimId: claimId
        }

        const claims: ISelectOption<string>[] = statementRows.filter(s=>s.rowType === 'claim').map(s=>{
            return {
                label: s.date + ' ' + s.text,
                value: s.claimId
            }  as ISelectOption<string>
        });

        async function onSubmit(resp: any): Promise<void> {
            console.log("patientStatementDetails handleEdit onSubmit");
            if(props.onUpdateCollectionPayment) {
                await props.onUpdateCollectionPayment(AmountFromJSON({amount: resp.amount, currency: 'USD'}), collectionPaymentId, resp.claimId);
            }
            dialogManager.close();
        }

        function onCancel(): void {
            console.log("patientStatementDetails handleEdit onCancel");
            dialogManager.close();
            return;
        }

        dialogManager.open({
            props: {
                renderTitle: 'Edit Patient Payment'
            },
            Component: (<StandardDialog schema={schema} initialValues={initialValues}
                        handleSubmit={onSubmit}
                        handleCancel={onCancel}
                        fields={
                            [
                                {
                                    prop: 'claimId',
                                    component: SelectField,
                                    label: 'Claim',
                                    options: claims
                                },
                                {
                                    prop: 'amount',
                                    label: 'Amount'
                                }
                            ]
            }/>)
        });
    }

    const handleOpenAllocate = (collectionPaymentSourceId: string, collectionPaymentSourceAmount: string) => {

        const schema = Yup.object().shape({
            claimId: Yup.string().required('Claim is required'),
            amount: Yup.string().required('Amount is required')
        });

        const initialValues = {
            claimId: undefined,
            amount: collectionPaymentSourceAmount
        }

        async function onSubmit(resp: any): Promise<void> {
            console.log("patientStatementDetails handleOpenAllocate onSubmit");
            if(props.onAllocate) {
                await props.onAllocate(collectionPaymentSourceId, AmountFromJSON({amount: resp.amount, currency: 'USD'}), resp.claimId);
            }
            dialogManager.close();
        }

        function onCancel(): void {
            console.log("patientStatementDetails handleOpenAllocate onCancel");
            dialogManager.close();
            return;
        }

        const claims: ISelectOption<string>[] = statementRows.filter(s=>s.rowType === 'claim').map(s=>{
            return {
                label: s.date + ' ' + s.text,
                value: s.claimId
            }  as ISelectOption<string>
        });

        dialogManager.open({
            props: {
                renderTitle: 'Allocate Payment to Claim'
            },
            Component: (<StandardDialog schema={schema} initialValues={initialValues}
                                        handleSubmit={onSubmit}
                                        handleCancel={onCancel}
                                        fields={
                [
                    {
                        prop: 'claimId',
                        component: SelectField,
                        label: 'Claim',
                        options: claims
                    },
                    {
                        prop: 'amount',
                        label: 'Amount'
                    }
                ]
            }/>)
        });
    }

    function renderElementText(statementRow: StatementRow): React.ReactElement {
        if((!props.printMode) && props.showActions) {
            if(statementRow.rowType === 'claim') {
                return <>{statementRow.text} <b>{statementRow.pendingAdjudication && statementRow.rowType === 'claim' ? '(Pending Adjudication)' : ''}</b><ActionMenuBadge label="View" clickAction={() => handleOpenClaim(statementRow.claimId ? statementRow.claimId : '')}></ActionMenuBadge> </>
            } else if(statementRow.rowType === 'paymentSource') {
                return <>{statementRow.text} <ActionMenuBadge label="Allocate" clickAction={() => handleOpenAllocate(statementRow.collectionPaymentSourceId ? statementRow.collectionPaymentSourceId : '', statementRow.credit ? statementRow.credit : '')}></ActionMenuBadge></>
            } else if(statementRow.rowType === 'collectionPayment') {
                return <>{statementRow.text} <ActionMenuBadge label="Edit" clickAction={() => handleEdit(statementRow.collectionPaymentId ? statementRow.collectionPaymentId : '', statementRow.credit ? statementRow.credit : '', statementRow.claimId ? statementRow.claimId : '')}></ActionMenuBadge></>
            } else {
                return <>{statementRow.text}</>;
            }
        } else {
            return <>{statementRow.text} <b>{statementRow.pendingAdjudication && statementRow.rowType === 'claim' ? '(Pending Adjudication)' : ''}</b></>;
        }
    }

    if(patientStatement.paymentSources) {
        for (const paymentSource of patientStatement.paymentSources) {
            if(paymentSource.unallocatedAmount && paymentSource.unallocatedAmount.amount !== '0.00') {
                const text = paymentSource.unallocatedAmount && paymentSource.unallocatedAmount.amount && parseInt(paymentSource.unallocatedAmount.amount.replace('.', '')) > 0 ? 'Patient Payment (Unallocated)' : 'Patient Refund (Unallocated)';

                let negatedAmount = paymentSource.unallocatedAmount && paymentSource.unallocatedAmount.amount ? paymentSource.unallocatedAmount.amount : '0.00';
                if(negatedAmount != '0.00') {
                    if(negatedAmount.indexOf('-') > -1) {
                        negatedAmount = negatedAmount.replace("-", "");
                    } else {
                        negatedAmount = '-' + negatedAmount;
                    }
                }

                const unallocatedPaymentStatementRow: StatementRow = {
                    id: 'patientPayment' + paymentSource.id,
                    text: text,
                    date: paymentSource.paymentDate,
                    credit: paymentSource.unallocatedAmount ? paymentSource.unallocatedAmount.amount : '0.00',
                    balance: negatedAmount,
                    dateKey: getDateKey(paymentSource.paymentDate, 'Z'),
                    rowType: 'paymentSource',
                    collectionPaymentSourceId: paymentSource.id
                };

                statementRows.push(unallocatedPaymentStatementRow);
                hasUnallocatedPaymentSources = true;
            }
        }
    }

    if(patientStatement.claims) {
        for (const patientStatementClaim of patientStatement.claims) {
            let dateGroup = getDateGroup(patientStatementClaim.fromDos);
            const renderingProviderString: string = getProviderString(patientStatementClaim.renderingProviders);
            statementRows.push({
                id: 'claimDetails' + patientStatementClaim.externalId,
                date: getDateString(patientStatementClaim.fromDos, patientStatementClaim.throughDos),
                text: 'Claim ' + patientStatementClaim.externalId + '' + (renderingProviderString.length > 0 ? ', Provider(s): ' + renderingProviderString : ''),
                dateKey: dateGroup,
                rowType: 'claim',
                claimId: patientStatementClaim.id,
                pendingAdjudication: patientStatementClaim.pendingAdjudication
            });
            //(patientStatementClaim.pendingAdjudication ? ' (Pending Adjudication)' : '')

            dateGroup = getDateGroup(patientStatementClaim.fromDos);

            if(patientStatementClaim.serviceLines) {
                for (const serviceLine of patientStatementClaim.serviceLines) {
                    let descriptionText: string = '';
                    if(serviceLine.procedure) {
                        descriptionText+= serviceLine.procedure;
                    }
                    if(serviceLine.description) {
                        if(descriptionText.length > 0) {
                            descriptionText+= ' ';
                        }
                        descriptionText+= serviceLine.description;
                    }
                    statementRows.push({
                        id: 'claim' + patientStatementClaim.externalId + serviceLine.id,
                        text: descriptionText,
                        charge: serviceLine.chargeAmount ? serviceLine.chargeAmount.amount : '',
                        dateKey: dateGroup,
                        pendingAdjudication: patientStatementClaim.pendingAdjudication
                    });
                }
            }

            dateGroup = getDateGroup(patientStatementClaim.fromDos);

            if(patientStatementClaim.discountLines) {
                for(const discountPayment of patientStatementClaim.discountLines) {
                    statementRows.push({
                        id: 'discountPayment' + discountPayment.id,
                        text: 'Discount',
                        date: discountPayment.paymentDate,
                        credit: discountPayment.amount ? discountPayment.amount.amount : '0.00',
                        dateKey: dateGroup,
                        pendingAdjudication: patientStatementClaim.pendingAdjudication
                    });
                }
            }

            dateGroup = getDateGroup(patientStatementClaim.fromDos);

            if(patientStatementClaim.payerPayments) {
                for(const payerPayment of patientStatementClaim.payerPayments) {
                    statementRows.push({
                        id: 'payerPayment' + payerPayment.id,
                        text: payerPayment.insuranceName + ' Payment',
                        date: payerPayment.paymentDate,
                        credit: payerPayment.payerAmount ? payerPayment.payerAmount.amount : '0.00',
                        dateKey: dateGroup,
                        pendingAdjudication: patientStatementClaim.pendingAdjudication
                    });
                    statementRows.push({
                        id: 'payerAdjustment' + payerPayment.id,
                        text: payerPayment.insuranceName + ' Adjustment',
                        date: payerPayment.paymentDate,
                        credit: payerPayment.adjustmentAmount ? payerPayment.adjustmentAmount.amount : '0.00',
                        dateKey: dateGroup,
                        pendingAdjudication: patientStatementClaim.pendingAdjudication
                    });
                }
            }

            if(patientStatementClaim.patientPayments) {
                for(const patientPayment of patientStatementClaim.patientPayments) {
                    const text = patientPayment.amount && patientPayment.amount.amount && parseInt(patientPayment.amount.amount.replace('.', '')) > 0 ? 'Patient Payment for Claim ' + patientStatementClaim.externalId : 'Patient Refund for Claim ' + patientStatementClaim.externalId;
                    statementRows.push({
                        id: 'patientPayment' + patientPayment.id,
                        text: text,
                        date: patientPayment.paymentDate,
                        credit: patientPayment.amount ? patientPayment.amount.amount : '0.00',
                        dateKey: getDateKey(patientPayment.paymentDate, 'Y'),
                        rowType: 'collectionPayment',
                        claimId: patientPayment.claimId,
                        collectionPaymentId: patientPayment.id,
                        pendingAdjudication: patientStatementClaim.pendingAdjudication
                    });
                }
            }

            dateGroup = getDateGroup(patientStatementClaim.fromDos);

            if(patientStatementClaim.balanceDue) {
                statementRows.push({
                    id: 'balance' + patientStatementClaim.externalId,
                    text: 'Your Balance Currently Due On This Claim ...',
                    balance: patientStatementClaim.balanceDue.amount,
                    dateKey: dateGroup,
                    pendingAdjudication: patientStatementClaim.pendingAdjudication
                })
            }
        }
    }

    //Sort descending by dateKey
    statementRows.sort((a, b) => b.dateKey.localeCompare(a.dateKey));

    return (

        <S.StyledPageLayoutRow>
            <table style={{border: '1px solid #000'}}>
                <tbody>
                    <tr>
                        <td style={{border: '1px solid #000', verticalAlign: 'top'}}>
                            <div style={{fontWeight: 'bold', marginBottom: '10px', textTransform: 'uppercase'}}>Patient Information</div>
                            {patientStatement.patient && (
                                <div>{patientStatement.patient.nickname ? patientStatement.patient.nickname : `${patientStatement.patient.first} ${patientStatement.patient.middle ? patientStatement.patient.middle : ''} ${patientStatement.patient.last}`}</div>)
                            }
                            {patientStatement.patient && patientStatement.patient.addresses && patientStatement.patient.addresses.length > 0 &&
                                patientStatement.patient.addresses[0].lines && patientStatement.patient.addresses[0].lines.length > 0 && (
                                    <div>{patientStatement.patient.addresses[0].lines[0]}</div>)
                            }
                            {patientStatement.patient && patientStatement.patient.addresses && patientStatement.patient.addresses.length > 0 &&
                                patientStatement.patient.addresses[0].lines && patientStatement.patient.addresses[0].lines.length > 1 && (
                                    <div>{patientStatement.patient.addresses[0].lines[1]}</div>)
                            }
                            {patientStatement.patient && patientStatement.patient.addresses && patientStatement.patient.addresses.length > 0 &&
                                (
                                    <div>{patientStatement.patient.addresses[0].city}, {patientStatement.patient.addresses[0].state} {patientStatement.patient.addresses[0].postal}</div>)
                            }
                        </td>
                        <td style={{border: '1px solid #000', verticalAlign: 'top'}}>
                            <div style={{fontWeight: 'bold', marginBottom: '10px', textTransform: 'uppercase'}}>THIS IS A STATEMENT OF SERVICES RENDERED BY PHYSICIAN(S) WHO ARE MEMBERS OF:</div>
                            {patientStatement.billingProvider && patientStatement.billingProvider.organization && (
                                <div>{patientStatement.billingProvider.organization}</div>)
                            }
                            {patientStatement.billingProvider && patientStatement.billingProvider.first && (
                                <div>{patientStatement.billingProvider.first} {patientStatement.billingProvider.middle} {patientStatement.billingProvider.last}</div>)
                            }
                            {patientStatement.billingProvider && patientStatement.billingProvider.addresses && patientStatement.billingProvider.addresses.length > 0 &&
                                patientStatement.billingProvider.addresses[0].lines && patientStatement.billingProvider.addresses[0].lines.length > 0 && (
                                    <div>{patientStatement.billingProvider.addresses[0].lines[0]}</div>)
                            }
                            {patientStatement.billingProvider && patientStatement.billingProvider.addresses && patientStatement.billingProvider.addresses.length > 0 &&
                                patientStatement.billingProvider.addresses[0].lines && patientStatement.billingProvider.addresses[0].lines.length > 1 && (
                                    <div>{patientStatement.billingProvider.addresses[0].lines[1]}</div>)
                            }
                            {patientStatement.billingProvider && patientStatement.billingProvider.addresses && patientStatement.billingProvider.addresses.length > 0 &&
                                (
                                    <div>{patientStatement.billingProvider.addresses[0].city}, {patientStatement.billingProvider.addresses[0].state} {patientStatement.billingProvider.addresses[0].postal}</div>)
                            }
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={2} style={{border: '1px solid #000', verticalAlign: 'top'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead style={{textAlign: 'left'}}>
                                    <tr>
                                            <th>Date</th>
                                            <th>Description</th>
                                            <th>Charge</th>
                                            <th>Credit</th>
                                            <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                {statementRows.map(item => (
                                    <tr key={item.id} style={{backgroundColor: item.pendingAdjudication ? '#ddd': '#fff'}}>
                                        <td>{item.date}</td>
                                        <td>{renderElementText(item)}</td>
                                        <td>{item.charge}</td>
                                        <td>{item.credit}</td>
                                        <td>{item.balance}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={2} style={{border: '1px solid #000', verticalAlign: 'top'}}>
                            <table style={{width: '100%'}}>
                                <thead style={{fontWeight: 'bold', textTransform: 'uppercase'}}>
                                    <tr>
                                        <th>
                                            Current Date
                                        </th>
                                        <th>
                                            Patient
                                        </th>
                                        <th>
                                            Account #
                                        </th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td style={{textTransform: 'uppercase', textAlign: 'center'}}>
                                        {getCurrentDate()}
                                    </td>
                                    <td style={{textTransform: 'uppercase', textAlign: 'center'}}>
                                        {patientStatement.patient && (
                                            <span>{patientStatement.patient.nickname ? patientStatement.patient.nickname : `${patientStatement.patient.first} ${patientStatement.patient.middle ? patientStatement.patient.middle: ''} ${patientStatement.patient.last}`}</span>)
                                        }
                                    </td>
                                    <td style={{textTransform: 'uppercase', textAlign: 'center'}}>
                                        {patientStatement.patient && (
                                            <span>{patientStatement.patient.externalId}</span>)
                                        }
                                    </td>
                                    <td style={{fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center'}}>
                                        <span>CURRENT BALANCE</span>
                                    </td>
                                    <td style={{fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'left'}}>
                                        {patientStatement.balanceDue && (
                                            <span>{patientStatement.balanceDue.amount}</span>)
                                        }
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                    <tr colspan="100%" style={{fontSize: '0.8em', padding: 5}}>
                        Note(s): The gray highlighted rows are pending adjudication and not reflected in the current balance. Your current balance may not be due as a refund if you have pending items.
                    </tr>
                </tbody>
            </table>
        </S.StyledPageLayoutRow>
    );
};

